import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  ShoppingBag, Plus, Search, Trash2, Package, X, Pencil, Printer,
  DollarSign, CheckCircle, Ban, RefreshCw, Layers, ChevronDown
} from 'lucide-react';
import { Gate } from './AccessStudio';

// Tab-view permissions — scanned by scan-gates.js so store.<feature>.view lands
// in the permission catalog and can be granted per role in the matrix.
const STORE_TAB_PERMISSIONS = {
  orders: 'store.orders.view',
  cashier: 'store.payments.view',
  catalog: 'store.catalog.view',
};

// Store module — uniforms/books point-of-sale.
//   Orders  (store staff): build a PENDING order for a family.
//   Cashier (payments):    accept payment for a pending order + print receipt.
//   Catalog (admin):       manage products / sizes / prices / stock.
// Standalone cash sale — never touches family balances.

const esc = (s) => String(s == null ? '' : s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const fmtDate = (d) => (d ? new Date(d).toLocaleString() : '');
const variantLabel = (v) => (v.Size ? `Size ${v.Size}` : 'One size');

// Sort sizes sensibly: numeric ascending (2,4,6…), then letter sizes in a
// natural order (Small → 6X-Large), then anything else alphabetically.
const LETTER_ORDER = ['xs', 'small', 's', 'medium', 'm', 'large', 'l', 'x-large', 'xl',
  '2x-large', 'xxl', '3x-large', '4x-large', '5x-large', '6x-large'];
const sizeRank = (size) => {
  const s = String(size ?? '').trim();
  const num = Number(s);
  if (s !== '' && !isNaN(num)) return [0, num, s];
  const idx = LETTER_ORDER.indexOf(s.toLowerCase());
  return [1, idx === -1 ? 999 : idx, s.toLowerCase()];
};
const sortVariants = (vs) => [...(vs || [])].sort((a, b) => {
  const ra = sizeRank(a.Size), rb = sizeRank(b.Size);
  return ra[0] - rb[0] || ra[1] - rb[1] || String(ra[2]).localeCompare(String(rb[2]));
});

// Laser A5 receipt — a single copy (print 2 from the print dialog if you want a
// file copy). Matches the look of the other module receipts.
function printReceipt(r) {
  const date = r.PaidAt ? new Date(r.PaidAt).toLocaleString() : new Date().toLocaleString();
  const rows = (r.items || []).map((it) => `
    <tr>
      <td>${esc(it.ProductName)}</td>
      <td class="c">${esc(it.Size || '—')}</td>
      <td class="r">${money(it.UnitPrice)}</td>
      <td class="c">${it.Quantity}</td>
      <td class="r">${money(it.LineTotal)}</td>
    </tr>`).join('');

  const html = `<!doctype html><html><head><meta charset="utf-8"><title>Store Receipt ${r.ReceiptNumber || ''}</title>
    <style>
      @page { size: A4 portrait; margin: 0; }
      * { box-sizing: border-box; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
      html, body { width: 210mm; margin: 0; padding: 0; }
      body { font-family: Arial, sans-serif; color: #1f2937; }
      /* Use only the TOP HALF of the A4 sheet (works on a default-A4 printer);
         a dashed line marks where to cut to an A5-size receipt. */
      .half { width: 210mm; height: 148mm; padding: 8mm 12mm; position: relative; border-bottom: 1px dashed #cbd5e1; overflow: hidden; }
      .cut { position: absolute; bottom: 2mm; left: 0; width: 100%; text-align: center; font-size: 9px; color: #9ca3af; }
      .head { text-align: center; border-bottom: 2px solid #1f2937; padding-bottom: 6px; margin-bottom: 10px; }
      .school { font-size: 16px; font-weight: bold; }
      .sub { font-size: 10px; color: #6b7280; margin-top: 2px; }
      .meta { display: grid; grid-template-columns: 1fr 1fr; gap: 3px 14px; font-size: 11px; margin-bottom: 10px; }
      .meta .lbl { color: #6b7280; display: inline-block; min-width: 58px; }
      table { width: 100%; border-collapse: collapse; }
      th, td { padding: 4px 6px; font-size: 11px; }
      thead th { text-align: left; border-bottom: 1.5px solid #1f2937; }
      tbody td { border-bottom: 1px solid #eef2f7; }
      .r { text-align: right; } .c { text-align: center; }
      tfoot .tot td { border-top: 1.5px solid #1f2937; font-weight: bold; font-size: 12px; padding-top: 6px; }
      tfoot td { font-size: 11px; }
      .sig { margin-top: 22px; width: 55%; }
      .sig .line { border-top: 1px solid #1f2937; margin-bottom: 3px; }
      .sig-label { font-size: 10px; color: #6b7280; }
      .inner { transform-origin: top left; }
    </style></head><body>
    <div class="half">
      <div class="inner">
      <div class="head">
        <div class="school">Collège des Apôtres</div>
        <div class="sub">Store — Uniforms &amp; Books</div>
      </div>
      <div class="meta">
        <div><span class="lbl">Family</span> ${esc(r.FamilyName || r.FamilyID)}</div>
        <div><span class="lbl">Receipt #</span> ${r.ReceiptNumber ?? ''}</div>
        <div><span class="lbl">Order #</span> ${r.OrderID}</div>
        <div><span class="lbl">Date</span> ${date}</div>
      </div>
      <table>
        <thead><tr><th>Product</th><th class="c">Size</th><th class="r">Unit Price</th><th class="c">Qty</th><th class="r">Total</th></tr></thead>
        <tbody>${rows}</tbody>
        <tfoot>
          <tr class="tot"><td colspan="4" class="r">TOTAL</td><td class="r">${money(r.TotalAmount)}</td></tr>
          <tr><td colspan="4" class="r">Paid</td><td class="r">${money(r.AmountPaid ?? r.TotalAmount)}</td></tr>
        </tfoot>
      </table>
      <div class="sig"><div class="line"></div><div class="sig-label">Signature</div></div>
      </div>
      <div class="cut">— — — — — — — — — — ✂ cut here — — — — — — — — — —</div>
    </div>
    <div id="probe" style="position:fixed;top:0;left:0;height:132mm;width:1px;visibility:hidden"></div>
    <script>
      (function(){
        function fit(){
          var inner=document.querySelector('.inner');
          var avail=document.getElementById('probe').offsetHeight;   // 194mm usable
          var actual=inner.offsetHeight;
          if(actual>avail){ inner.style.transform='scale('+(avail/actual)+')'; }
          setTimeout(function(){ window.focus(); window.print(); }, 80);
        }
        if(document.readyState==='complete') fit(); else window.addEventListener('load', fit);
      })();
    </script>
    </body></html>`;
  const win = window.open('', '', 'width=760,height=980');
  win.document.write(html);
  win.document.close();
}

const Store = ({ apiCall, selectedYear, hasRole, hasPermission, showSuccess, showError }) => {
  const canOrders = hasPermission('store', 'orders', 'view') || hasPermission('store', 'orders', 'create');
  const canCatalog = hasPermission('store', 'catalog', 'view') || hasPermission('store', 'catalog', 'manage');
  const canPay = hasPermission('store', 'payments', 'view') || hasPermission('store', 'payments', 'process');

  const firstTab = canOrders ? 'orders' : canPay ? 'cashier' : canCatalog ? 'catalog' : 'orders';
  const [tab, setTab] = useState(firstTab);

  return (
    <div className="p-6 space-y-6">
      <div className="bg-white rounded-xl shadow-lg p-6 flex justify-between items-center">
        <div className="flex items-center gap-3">
          <ShoppingBag className="text-blue-600" size={28} />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Store</h1>
            <p className="text-gray-600 text-sm">Uniforms & books — orders, cashier, catalog</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Gate permission="store.orders.view" label="Store Orders Tab">
            <TabBtn id="orders" cur={tab} set={setTab} icon={<Package size={16} />} label="Orders" />
          </Gate>
          <Gate permission="store.payments.view" label="Store Cashier Tab">
            <TabBtn id="cashier" cur={tab} set={setTab} icon={<DollarSign size={16} />} label="Cashier" />
          </Gate>
          <Gate permission="store.catalog.view" label="Store Catalog Tab">
            <TabBtn id="catalog" cur={tab} set={setTab} icon={<Layers size={16} />} label="Catalog" />
          </Gate>
          <Gate permission="store.orders.view" label="Store Items Sold Tab">
            <TabBtn id="report" cur={tab} set={setTab} icon={<ShoppingBag size={16} />} label="Sold" />
          </Gate>
        </div>
      </div>

      {tab === 'orders' && <OrdersTab apiCall={apiCall} selectedYear={selectedYear} hasPermission={hasPermission} hasRole={hasRole} showSuccess={showSuccess} showError={showError} />}
      {tab === 'cashier' && <CashierTab apiCall={apiCall} showSuccess={showSuccess} showError={showError} />}
      {tab === 'catalog' && <CatalogTab apiCall={apiCall} showSuccess={showSuccess} showError={showError} />}
      {tab === 'report' && <ReportTab apiCall={apiCall} showError={showError} />}
    </div>
  );
};

const TabBtn = ({ id, cur, set, icon, label }) => (
  <button onClick={() => set(id)}
    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold ${cur === id ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
    {icon}{label}
  </button>
);

// ---------------------------------------------------------------- Orders tab
// Items-sold report: modular date range, per-item summary, optional buyers list.
function ReportTab({ apiCall, showError }) {
  const today = new Date().toISOString().slice(0, 10);
  const [from, setFrom] = useState(today);
  const [to, setTo] = useState(today);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showBuyers, setShowBuyers] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await apiCall(`/store/items-sold?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`));
    } catch (e) { showError(e.message); }
    finally { setLoading(false); }
  }, [apiCall, from, to, showError]);

  // Initial load = today.
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3 bg-white p-4 rounded-lg border">
        <div>
          <label className="block text-xs text-gray-500 mb-1">From</label>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="border rounded px-3 py-2 text-sm" />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">To</label>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="border rounded px-3 py-2 text-sm" />
        </div>
        <button onClick={load} disabled={loading} className="px-4 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:opacity-50">
          {loading ? 'Loading…' : 'Show'}
        </button>
        <label className="flex items-center gap-2 text-sm ml-auto">
          <input type="checkbox" checked={showBuyers} onChange={(e) => setShowBuyers(e.target.checked)} />
          Show buyers
        </label>
      </div>

      {data && (
        <>
          <div className="flex gap-6 text-sm">
            <div><span className="text-gray-500">Items sold:</span> <span className="font-semibold">{data.totalQty}</span></div>
            <div><span className="text-gray-500">Revenue:</span> <span className="font-semibold text-blue-600">{money(data.totalRevenue)}</span></div>
          </div>

          <div className="bg-white rounded-lg border overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-gray-600"><tr>
                <th className="px-3 py-2 text-left">Product</th><th className="px-3 py-2 text-center">Size</th>
                <th className="px-3 py-2 text-left">Category</th><th className="px-3 py-2 text-center">Qty sold</th><th className="px-3 py-2 text-right">Revenue</th>
              </tr></thead>
              <tbody className="divide-y">
                {data.items.length === 0 ? (
                  <tr><td colSpan={5} className="px-3 py-6 text-center text-gray-400">No items sold in this range.</td></tr>
                ) : data.items.map((it, i) => (
                  <tr key={i}>
                    <td className="px-3 py-2">{it.ProductName}</td><td className="px-3 py-2 text-center">{it.Size || '—'}</td>
                    <td className="px-3 py-2">{it.Category}</td><td className="px-3 py-2 text-center font-medium">{it.QtySold}</td>
                    <td className="px-3 py-2 text-right">{money(it.Revenue)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {showBuyers && (
            <div className="bg-white rounded-lg border overflow-x-auto">
              <div className="px-3 py-2 text-xs font-semibold text-gray-500 border-b">Who bought — {data.sales.length} line(s)</div>
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-gray-600"><tr>
                  <th className="px-3 py-2 text-left">When</th><th className="px-3 py-2 text-center">Receipt</th>
                  <th className="px-3 py-2 text-left">Family</th><th className="px-3 py-2 text-left">Product</th>
                  <th className="px-3 py-2 text-center">Size</th><th className="px-3 py-2 text-center">Qty</th><th className="px-3 py-2 text-right">Total</th>
                </tr></thead>
                <tbody className="divide-y">
                  {data.sales.map((s, i) => (
                    <tr key={i}>
                      <td className="px-3 py-2">{fmtDate(s.PaidAt)}</td><td className="px-3 py-2 text-center">{s.ReceiptNumber != null ? s.ReceiptNumber : '—'}</td>
                      <td className="px-3 py-2">{s.FamilyName || s.FamilyID}</td><td className="px-3 py-2">{s.ProductName}</td>
                      <td className="px-3 py-2 text-center">{s.Size || '—'}</td><td className="px-3 py-2 text-center">{s.Quantity}</td>
                      <td className="px-3 py-2 text-right">{money(s.LineTotal)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function OrdersTab({ apiCall, selectedYear, hasPermission, hasRole, showSuccess, showError }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showBuilder, setShowBuilder] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detailId, setDetailId] = useState(null);

  const load = useCallback(async () => {
    try { setLoading(true); setOrders(await apiCall('/store/orders')); }
    catch (e) { showError('Failed to load orders: ' + e.message); }
    finally { setLoading(false); }
  }, [apiCall, showError]);
  useEffect(() => { load(); }, [load]);

  const cancel = async (id) => {
    if (!window.confirm('Cancel this pending order?')) return;
    try { await apiCall(`/store/orders/${id}/cancel`, { method: 'POST' }); showSuccess('Order cancelled'); load(); }
    catch (e) { showError(e.message); }
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="font-semibold text-gray-800">Orders</h2>
        <Gate permission="store.orders.create" label="New Store Order">
          <button onClick={() => { setEditing(null); setShowBuilder(true); }}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg">
            <Plus size={18} /> New Order
          </button>
        </Gate>
      </div>
      {loading ? <Spinner /> : orders.length === 0 ? (
        <p className="text-gray-500 text-sm">No orders yet.</p>
      ) : (
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-gray-600">
            <tr><th className="px-3 py-2 text-left">Order #</th><th className="px-3 py-2 text-left">Family</th>
              <th className="px-3 py-2 text-center">Items</th><th className="px-3 py-2 text-right">Total</th>
              <th className="px-3 py-2 text-center">Status</th><th className="px-3 py-2 text-left">Date</th><th></th></tr>
          </thead>
          <tbody className="divide-y">
            {orders.map((o) => (
              <tr key={o.OrderID} onClick={() => setDetailId(o.OrderID)} className="hover:bg-blue-50 cursor-pointer">
                <td className="px-3 py-2 font-semibold">#{o.OrderID}</td>
                <td className="px-3 py-2">{o.FamilyName || o.FamilyID}</td>
                <td className="px-3 py-2 text-center">{o.ItemCount}</td>
                <td className="px-3 py-2 text-right">{money(o.TotalAmount)}</td>
                <td className="px-3 py-2 text-center"><StatusBadge s={o.Status} /></td>
                <td className="px-3 py-2 text-gray-500">{fmtDate(o.CreatedAt)}</td>
                <td className="px-3 py-2 text-right">
                  {o.Status === 'Pending' && (
                    <div className="flex gap-2 justify-end" onClick={(e) => e.stopPropagation()}>
                      <Gate permission="store.orders.edit" label="Edit Store Order">
                        <button onClick={() => { setEditing(o.OrderID); setShowBuilder(true); }} className="text-blue-600 hover:text-blue-800" title="Edit"><Pencil size={16} /></button>
                      </Gate>
                      <Gate permission="store.orders.cancel" label="Cancel Store Order">
                        <button onClick={() => cancel(o.OrderID)} className="text-red-600 hover:text-red-800" title="Cancel"><Ban size={16} /></button>
                      </Gate>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {detailId && <OrderDetailModal id={detailId} apiCall={apiCall} onClose={() => setDetailId(null)}
        canExchange={hasPermission('store', 'orders', 'edit')} isAdmin={hasRole && hasRole('GlobalAdmin')}
        onChanged={load} showSuccess={showSuccess} showError={showError} />}
      {showBuilder && (
        <OrderBuilder apiCall={apiCall} selectedYear={selectedYear} editingId={editing}
          onClose={() => setShowBuilder(false)}
          onSaved={() => { setShowBuilder(false); load(); showSuccess('Order saved'); }}
          showError={showError} />
      )}
    </div>
  );
}

// Order details popup (click a row in Orders). Paid orders can have a line's
// SIZE exchanged (same product, same price) — restores old stock, deducts new.
function OrderDetailModal({ id, apiCall, onClose, canExchange, isAdmin, onChanged, showSuccess, showError }) {
  const [order, setOrder] = useState(null);
  const [products, setProducts] = useState([]);
  const [busy, setBusy] = useState(false);
  const [showItemExchange, setShowItemExchange] = useState(false);

  const reload = async () => {
    const o = await apiCall(`/store/orders/${id}`);
    setOrder(o);
    apiCall('/store/products').then(setProducts).catch(() => {});
    onChanged && onChanged();
  };
  useEffect(() => {
    apiCall(`/store/orders/${id}`).then(setOrder).catch((e) => { showError(e.message); onClose(); });
    apiCall('/store/products').then(setProducts).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const canDoExchange = canExchange && order && order.Status === 'Paid';

  // Same-product, same-price, in-stock sizes other than the item's own.
  const exchangeOptions = (it) => {
    const prod = products.find((p) => (p.variants || []).some((v) => v.VariantID === it.VariantID));
    if (!prod) return [];
    return (prod.variants || []).filter(
      (v) => v.VariantID !== it.VariantID && Number(v.UnitPrice) === Number(it.UnitPrice) && v.StockQuantity > 0
    );
  };

  const doExchange = async (it, newVariantId) => {
    const vid = parseInt(newVariantId, 10);
    if (!vid) return;
    const opt = exchangeOptions(it).find((v) => v.VariantID === vid);
    // For a multi-unit line, ask how many to exchange (the rest stays as-is).
    let qty = it.Quantity;
    if (it.Quantity > 1) {
      const ans = window.prompt(`This line has ${it.Quantity} units. How many to exchange to size ${opt ? opt.Size : ''}?`, '1');
      if (ans === null) return; // cancelled
      qty = parseInt(ans, 10);
      if (!Number.isInteger(qty) || qty < 1 || qty > it.Quantity) {
        showError(`Enter a quantity between 1 and ${it.Quantity}`);
        return;
      }
    }
    if (!window.confirm(
      `Exchange ${qty} × ${it.ProductName} — size ${it.Size} → size ${opt ? opt.Size : ''}?\n\nThe old size's stock is restored and the new size's is deducted. The price is unchanged.`
    )) return;
    setBusy(true);
    try {
      await apiCall(`/store/orders/${id}/items/${it.OrderItemID}/exchange`, {
        method: 'POST', body: JSON.stringify({ newVariantId: vid, quantity: qty })
      });
      showSuccess && showSuccess('Size exchanged');
      const o = await apiCall(`/store/orders/${id}`);
      setOrder(o);
      apiCall('/store/products').then(setProducts).catch(() => {});
      onChanged && onChanged();
    } catch (e) {
      showError(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="p-4 border-b flex justify-between items-center">
          <div>
            <h3 className="text-lg font-bold">Order #{id}{order?.ReceiptNumber ? <span className="ml-2 text-sm text-gray-500">Receipt #{order.ReceiptNumber}</span> : null}</h3>
            {order && <p className="text-sm text-gray-600">{order.FamilyName || order.FamilyID} · Family {order.FamilyID} · <StatusBadge s={order.Status} /></p>}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={22} /></button>
        </div>
        <div className="p-4 overflow-y-auto">
          {!order ? <Spinner /> : (
            <>
              <div className="grid grid-cols-2 gap-2 text-sm text-gray-600 mb-4">
                <div><span className="text-gray-400">Created:</span> {fmtDate(order.CreatedAt)}</div>
                {order.PaidAt && <div><span className="text-gray-400">Paid:</span> {fmtDate(order.PaidAt)}</div>}
                {order.AcademicYear && <div><span className="text-gray-400">Year:</span> {order.AcademicYear}</div>}
                {order.Notes && <div className="col-span-2"><span className="text-gray-400">Notes:</span> {order.Notes}</div>}
              </div>
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-gray-600"><tr>
                  <th className="px-3 py-2 text-left">Product</th><th className="px-3 py-2 text-center">Size</th>
                  <th className="px-3 py-2 text-right">Unit</th><th className="px-3 py-2 text-center">Qty</th><th className="px-3 py-2 text-right">Total</th>
                  {canDoExchange && <th className="px-3 py-2 text-center">Exchange size</th>}
                </tr></thead>
                <tbody className="divide-y">
                  {order.items.map((it) => {
                    const opts = canDoExchange ? exchangeOptions(it) : [];
                    return (
                      <tr key={it.OrderItemID}>
                        <td className="px-3 py-2">{it.ProductName}</td>
                        <td className="px-3 py-2 text-center">
                          {it.Size || '—'}
                          {it.ExchangedAt && (
                            <div className="text-[10px] text-amber-600" title={`Exchanged ${fmtDate(it.ExchangedAt)}`}>
                              ↔ was {it.PreviousSize || '?'} · {fmtDate(it.ExchangedAt)}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right">{money(it.UnitPrice)}</td><td className="px-3 py-2 text-center">{it.Quantity}</td>
                        <td className="px-3 py-2 text-right">{money(it.LineTotal)}</td>
                        {canDoExchange && (
                          <td className="px-3 py-2 text-center">
                            {opts.length === 0 ? (
                              <span className="text-xs text-gray-400">—</span>
                            ) : (
                              <select
                                disabled={busy}
                                value=""
                                onChange={(e) => doExchange(it, e.target.value)}
                                className="border rounded px-2 py-1 text-xs disabled:opacity-50"
                              >
                                <option value="">Change to…</option>
                                {opts.map((v) => (
                                  <option key={v.VariantID} value={v.VariantID}>
                                    Size {v.Size} ({v.StockQuantity} in stock)
                                  </option>
                                ))}
                              </select>
                            )}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {canDoExchange && (
                <p className="text-xs text-gray-400 mt-2">
                  The dropdown swaps to a same-price size only. To exchange for a
                  different product or quantity (paying any price difference), use
                  <b> Exchange items…</b> below.
                </p>
              )}
              {order.OriginalOrderID && (
                <p className="text-xs text-amber-600 mt-2">↔ This is an exchange booked against order #{order.OriginalOrderID}. Negative lines are items returned.</p>
              )}
              <div className="flex items-center justify-between mt-4">
                {canDoExchange && !order.OriginalOrderID ? (
                  <button onClick={() => setShowItemExchange(true)}
                    className="px-3 py-2 text-sm rounded-lg border border-blue-600 text-blue-600 hover:bg-blue-50">
                    Exchange items…
                  </button>
                ) : <span />}
                <div className="text-right text-xl font-bold">Total: <span className="text-blue-600">{money(order.TotalAmount)}</span></div>
              </div>
            </>
          )}
        </div>
      </div>
      {showItemExchange && order && (
        <ItemExchangeModal
          apiCall={apiCall} order={order} products={products} isAdmin={isAdmin}
          onClose={() => setShowItemExchange(false)}
          onDone={(result) => {
            setShowItemExchange(false);
            const net = Number(result.NetAmount);
            showSuccess && showSuccess(
              `Exchange order #${result.ExchangeOrderID} sent to cashier` +
              (net > 0 ? ` — to collect ${money(net)}` : net < 0 ? ` — to refund ${money(-net)}` : ' — no difference'));
            reload();
          }}
          showError={showError} />
      )}
    </div>
  );
}

// Exchange line(s) from a PAID order for different product(s)/qty. Books a new
// today-dated receipt for the net difference; the original order is untouched.
// Refund (net < 0) is admin-only — enforced by the backend, previewed here.
function ItemExchangeModal({ apiCall, order, products, isAdmin, onClose, onDone, showError }) {
  const [returns, setReturns] = useState({});   // OrderItemID -> qty to return
  const [cart, setCart] = useState([]);         // {variantId, productName, size, unitPrice, quantity, stock}
  const [expanded, setExpanded] = useState(null);
  const [busy, setBusy] = useState(false);

  // How many units of each paid line can still be returned.
  const returnable = (it) => Math.max(0, Number(it.Quantity) - Number(it.ReturnedQuantity || 0));

  const setReturnQty = (it, q) => {
    const max = returnable(it);
    let n = Math.max(0, Math.min(max, parseInt(q, 10) || 0));
    setReturns((r) => ({ ...r, [it.OrderItemID]: n }));
  };

  const addToCart = (variant, product) => setCart((c) => {
    const i = c.findIndex((x) => x.variantId === variant.VariantID);
    if (i >= 0) {
      if (c[i].quantity >= variant.StockQuantity) return c;
      const n = [...c]; n[i] = { ...n[i], quantity: n[i].quantity + 1 }; return n;
    }
    if (variant.StockQuantity <= 0) return c;
    return [...c, { variantId: variant.VariantID, productName: product.Name, size: variant.Size, unitPrice: Number(variant.UnitPrice), quantity: 1, stock: variant.StockQuantity }];
  });
  const setCartQty = (variantId, q) => setCart((c) => c.map((x) => {
    if (x.variantId !== variantId) return x;
    let n = Math.max(1, parseInt(q, 10) || 1);
    if (x.stock != null) n = Math.min(x.stock, n);
    return { ...x, quantity: n };
  }));
  const removeCart = (variantId) => setCart((c) => c.filter((x) => x.variantId !== variantId));

  const returnCredit = (order.items || []).reduce((s, it) => s + (returns[it.OrderItemID] || 0) * Number(it.UnitPrice), 0);
  const newCost = cart.reduce((s, x) => s + x.quantity * x.unitPrice, 0);
  const net = newCost - returnCredit;
  const hasReturns = Object.values(returns).some((q) => q > 0);
  const hasNew = cart.length > 0;
  const refundBlocked = net < 0 && !isAdmin;

  const submit = async () => {
    if (!hasReturns || !hasNew) { showError('Pick at least one item to return and one to give in exchange.'); return; }
    if (refundBlocked) { showError('This exchange refunds money — an administrator must process it.'); return; }
    const body = {
      returns: (order.items || []).filter((it) => returns[it.OrderItemID] > 0)
        .map((it) => ({ orderItemId: it.OrderItemID, qty: returns[it.OrderItemID] })),
      newItems: cart.map((x) => ({ variantId: x.variantId, qty: x.quantity })),
    };
    setBusy(true);
    try {
      const r = await apiCall(`/store/orders/${order.OrderID}/exchange-items`, { method: 'POST', body: JSON.stringify(body) });
      onDone(r);
    } catch (e) { showError(e.message); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[92vh] flex flex-col">
        <div className="p-4 border-b flex justify-between items-center">
          <h3 className="text-lg font-bold">Exchange items — order #{order.OrderID}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={22} /></button>
        </div>
        <div className="p-4 overflow-y-auto grid md:grid-cols-2 gap-6">
          {/* LEFT: choose what to return */}
          <div>
            <h4 className="font-semibold text-sm mb-2">1 · Give back</h4>
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-gray-600"><tr>
                <th className="px-2 py-1 text-left">Product</th><th className="px-2 py-1 text-center">Size</th>
                <th className="px-2 py-1 text-right">Paid</th><th className="px-2 py-1 text-center">Return</th>
              </tr></thead>
              <tbody className="divide-y">
                {(order.items || []).map((it) => {
                  const max = returnable(it);
                  return (
                    <tr key={it.OrderItemID} className={max === 0 ? 'opacity-40' : ''}>
                      <td className="px-2 py-1">{it.ProductName}</td>
                      <td className="px-2 py-1 text-center">{it.Size || '—'}</td>
                      <td className="px-2 py-1 text-right">{money(it.UnitPrice)}</td>
                      <td className="px-2 py-1 text-center">
                        <input type="number" min="0" max={max} disabled={max === 0}
                          value={returns[it.OrderItemID] || 0}
                          onChange={(e) => setReturnQty(it, e.target.value)}
                          className="w-16 border rounded px-1 py-0.5 text-center" />
                        <div className="text-[10px] text-gray-400">of {max}</div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {/* RIGHT: choose new items */}
          <div>
            <h4 className="font-semibold text-sm mb-2">2 · Take instead</h4>
            <div className="max-h-56 overflow-y-auto border rounded-lg divide-y">
              {products.map((p) => (
                <div key={p.ProductID}>
                  <button onClick={() => setExpanded(expanded === p.ProductID ? null : p.ProductID)}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex justify-between">
                    <span>{p.Name}</span><span className="text-gray-400 text-xs">{p.Category || ''}</span>
                  </button>
                  {expanded === p.ProductID && (
                    <div className="px-3 pb-2 flex flex-wrap gap-1">
                      {sortVariants(p.variants).filter((v) => v.StockQuantity > 0).map((v) => (
                        <button key={v.VariantID} onClick={() => addToCart(v, p)}
                          className="px-2 py-1 text-xs border rounded hover:bg-blue-50">
                          {variantLabel(v)} · {money(v.UnitPrice)} <span className="text-gray-400">({v.StockQuantity})</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
            {cart.length > 0 && (
              <table className="min-w-full text-sm mt-2">
                <tbody className="divide-y">
                  {cart.map((x) => (
                    <tr key={x.variantId}>
                      <td className="px-2 py-1">{x.productName}<span className="text-gray-400"> · {x.size || 'one size'}</span></td>
                      <td className="px-2 py-1 text-right">{money(x.unitPrice)}</td>
                      <td className="px-2 py-1 text-center">
                        <input type="number" min="1" max={x.stock} value={x.quantity}
                          onChange={(e) => setCartQty(x.variantId, e.target.value)}
                          className="w-14 border rounded px-1 py-0.5 text-center" />
                      </td>
                      <td className="px-2 py-1 text-right"><button onClick={() => removeCart(x.variantId)} className="text-red-500 text-xs">remove</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
        {/* Footer: money summary + submit */}
        <div className="p-4 border-t">
          <div className="flex flex-wrap justify-end gap-x-6 gap-y-1 text-sm mb-3">
            <span className="text-gray-500">Return credit: <b className="text-gray-700">{money(returnCredit)}</b></span>
            <span className="text-gray-500">New items: <b className="text-gray-700">{money(newCost)}</b></span>
            <span className={net > 0 ? 'text-blue-700' : net < 0 ? 'text-red-600' : 'text-gray-700'}>
              {net > 0 ? <>Customer pays: <b>{money(net)}</b></> : net < 0 ? <>Refund to customer: <b>{money(-net)}</b></> : <b>Even exchange — no difference</b>}
            </span>
          </div>
          <p className="text-xs text-gray-400 text-right mb-2">Creates a pending exchange for the cashier — stock moves and the receipt prints when they settle it.</p>
          {refundBlocked && (
            <p className="text-xs text-red-600 text-right mb-2">This exchange refunds money — only a GlobalAdmin can process it.</p>
          )}
          <div className="flex justify-end gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm rounded-lg border">Cancel</button>
            <button onClick={submit} disabled={busy || !hasReturns || !hasNew || refundBlocked}
              className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white disabled:opacity-50">
              {busy ? 'Working…' : net < 0 ? 'Send to cashier (refund)' : net > 0 ? 'Send to cashier (collect)' : 'Send to cashier'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Order builder modal: family search + product/size/qty cart.
function OrderBuilder({ apiCall, selectedYear, editingId, onClose, onSaved, showError }) {
  const [famSearch, setFamSearch] = useState('');
  const [famResults, setFamResults] = useState([]);
  const [family, setFamily] = useState(null);
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState([]);       // {variantId, productName, size, unitPrice, quantity}
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(null); // productId whose sizes are shown

  useEffect(() => { apiCall('/store/products').then(setProducts).catch(() => {}); }, [apiCall]);

  // Current stock per variant (from the catalog) — used to cap quantities.
  const stockByVariant = useMemo(() => {
    const m = {};
    products.forEach((p) => (p.variants || []).forEach((v) => { m[v.VariantID] = v.StockQuantity; }));
    return m;
  }, [products]);

  // Load an existing order to edit.
  useEffect(() => {
    if (!editingId) return;
    apiCall(`/store/orders/${editingId}`).then((o) => {
      setFamily({ FamilyID: o.FamilyID, FatherName: o.FamilyName });
      setCart(o.items.map((it) => ({ variantId: it.VariantID, productName: it.ProductName, size: it.Size, unitPrice: Number(it.UnitPrice), quantity: it.Quantity })));
    }).catch((e) => showError(e.message));
  }, [editingId, apiCall, showError]);

  useEffect(() => {
    if (famSearch.length < 2 || family) { setFamResults([]); return; }
    const t = setTimeout(async () => {
      try { setFamResults(await apiCall(`/store/search-families?query=${encodeURIComponent(famSearch)}&academicYear=${encodeURIComponent(selectedYear || '')}`)); }
      catch { /* ignore */ }
    }, 300);
    return () => clearTimeout(t);
  }, [famSearch, family, apiCall, selectedYear]);

  const addToCart = (variant, product) => {
    const stock = variant.StockQuantity;
    setCart((c) => {
      const i = c.findIndex((x) => x.variantId === variant.VariantID);
      if (i >= 0) {
        if (c[i].quantity >= stock) return c; // already at stock — don't exceed
        const n = [...c]; n[i] = { ...n[i], quantity: n[i].quantity + 1 }; return n;
      }
      if (stock <= 0) return c;
      return [...c, { variantId: variant.VariantID, productName: product.Name, size: variant.Size, unitPrice: Number(variant.UnitPrice), quantity: 1 }];
    });
  };
  // Clamp to [1, stock] so you can never order more than is in stock.
  const setQty = (variantId, q) => setCart((c) => c.map((x) => {
    if (x.variantId !== variantId) return x;
    const stock = stockByVariant[variantId];
    let n = Math.max(1, q || 1);
    if (stock != null) n = Math.min(stock, n);
    return { ...x, quantity: n };
  }));
  const removeItem = (variantId) => setCart((c) => c.filter((x) => x.variantId !== variantId));
  const total = cart.reduce((s, x) => s + x.unitPrice * x.quantity, 0);

  const save = async () => {
    if (!family) return showError('Select a family');
    if (cart.length === 0) return showError('Add at least one item');
    setSaving(true);
    try {
      const body = { familyId: family.FamilyID, academicYear: selectedYear, items: cart.map((x) => ({ variantId: x.variantId, quantity: x.quantity })) };
      if (editingId) await apiCall(`/store/orders/${editingId}`, { method: 'PUT', body: JSON.stringify(body) });
      else await apiCall('/store/orders', { method: 'POST', body: JSON.stringify(body) });
      onSaved();
    } catch (e) { showError(e.message); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        <div className="p-4 border-b flex justify-between items-center">
          <h3 className="text-lg font-bold">{editingId ? `Edit Order #${editingId}` : 'New Order'}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={22} /></button>
        </div>
        <div className="p-4 overflow-y-auto space-y-4">
          {/* Family */}
          {family ? (
            <div className="bg-green-50 border-l-4 border-green-500 p-3 rounded-r flex justify-between items-center">
              <span className="text-sm text-green-800"><strong>{family.FatherName || family.FamilyID}</strong> · Family {family.FamilyID}</span>
              {!editingId && <button onClick={() => { setFamily(null); setFamResults([]); }} className="text-xs text-blue-600 underline">change</button>}
            </div>
          ) : (
            <div className="relative">
              <input value={famSearch} onChange={(e) => setFamSearch(e.target.value)} placeholder="Search family (name, ID, phone)…"
                className="w-full pl-10 pr-4 py-2 border rounded-lg" />
              <Search size={18} className="absolute left-3 top-2.5 text-gray-400" />
              {famResults.length > 0 && (
                <div className="w-full mt-1 bg-white border rounded-lg shadow max-h-56 overflow-y-auto">
                  {famResults.map((f) => (
                    <button key={f.FamilyID} onClick={() => { setFamily(f); setFamSearch(''); setFamResults([]); }}
                      className="w-full text-left px-4 py-2 hover:bg-blue-50 border-b text-sm">
                      <div><span className="font-semibold">{f.FatherName || f.FamilyID}</span> <span className="text-gray-500">· {f.FamilyID}{f.PrimaryPhone ? ` · ${f.PrimaryPhone}` : ''}</span></div>
                      {f.Children && <div className="text-xs text-gray-500 mt-0.5">🎒 {f.Children}</div>}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Product picker + cart */}
          {family && (
            <div className="grid grid-cols-2 gap-4">
              <div className="border rounded-lg p-3 max-h-80 overflow-y-auto">
                <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Products</p>
                {products.map((p) => {
                  const open = expanded === p.ProductID;
                  return (
                    <div key={p.ProductID} className="mb-1 border rounded-lg overflow-hidden">
                      <button type="button" onClick={() => setExpanded(open ? null : p.ProductID)}
                        className="w-full flex justify-between items-center px-3 py-2 text-sm font-semibold text-gray-800 hover:bg-gray-50">
                        <span>{p.Name}</span>
                        <ChevronDown size={16} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
                      </button>
                      {open && (
                        <div className="grid grid-cols-4 gap-2 px-3 pb-3">
                          {sortVariants(p.variants).map((v) => {
                            const inCart = cart.find((x) => x.variantId === v.VariantID);
                            const inQty = inCart ? inCart.quantity : 0;
                            const remaining = v.StockQuantity - inQty;
                            const out = remaining <= 0;
                            const low = v.StockQuantity <= 5;
                            return (
                              <button key={v.VariantID} onClick={() => addToCart(v, p)} disabled={out}
                                className={`relative rounded-lg border p-2 text-center transition
                                  ${out ? 'opacity-40 bg-gray-50 cursor-not-allowed' : 'hover:border-blue-500 hover:bg-blue-50'}
                                  ${inQty > 0 ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-300' : 'border-gray-200'}`}>
                                {inQty > 0 && (
                                  <span className="absolute -top-1.5 -right-1.5 bg-blue-600 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">{inQty}</span>
                                )}
                                <div className="text-sm font-bold text-gray-800 leading-tight truncate">{v.Size || 'One'}</div>
                                <div className="text-[11px] text-gray-500">{money(v.UnitPrice)}</div>
                                <div className={`text-[10px] ${low ? 'text-red-600 font-semibold' : 'text-gray-400'}`}>{v.StockQuantity} in stock</div>
                              </button>
                            );
                          })}
                          {(p.variants || []).length === 0 && <span className="col-span-4 text-xs text-gray-400">No sizes yet.</span>}
                        </div>
                      )}
                    </div>
                  );
                })}
                {products.length === 0 && <p className="text-xs text-gray-400">No products in catalog yet.</p>}
              </div>
              <div className="border rounded-lg p-3">
                <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Order</p>
                {cart.length === 0 ? <p className="text-xs text-gray-400">Pick products on the left.</p> : (
                  <div className="space-y-2">
                    {cart.map((x) => (
                      <div key={x.variantId} className="flex items-center gap-2 text-sm">
                        <span className="flex-1">{x.productName}{x.size ? ` · ${x.size}` : ''}</span>
                        <input type="number" min={1} max={stockByVariant[x.variantId] ?? undefined} value={x.quantity}
                          onChange={(e) => setQty(x.variantId, parseInt(e.target.value, 10) || 1)}
                          className="w-14 border rounded px-1 py-0.5 text-center" />
                        <span className="w-16 text-right">{money(x.unitPrice * x.quantity)}</span>
                        <button onClick={() => removeItem(x.variantId)} className="text-red-500"><Trash2 size={14} /></button>
                      </div>
                    ))}
                    <div className="border-t pt-2 flex justify-between font-bold"><span>Total</span><span className="text-blue-600">{money(total)}</span></div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        <div className="p-4 border-t flex gap-3">
          <button onClick={save} disabled={saving || !family || cart.length === 0}
            className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 rounded-lg disabled:opacity-50">
            {saving ? 'Saving…' : editingId ? 'Save Changes' : 'Create Order'}
          </button>
          <button onClick={onClose} className="px-6 bg-gray-200 hover:bg-gray-300 rounded-lg">Cancel</button>
        </div>
      </div>
    </div>
  );
}

// --------------------------------------------------------------- Cashier tab
function CashierTab({ apiCall, showSuccess, showError }) {
  const [view, setView] = useState('pending'); // 'pending' | 'paid'
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // full order
  const [paying, setPaying] = useState(false);

  const load = useCallback(async () => {
    try { setLoading(true); setList(await apiCall(`/store/orders?status=${view === 'paid' ? 'Paid' : 'Pending'}`)); }
    catch (e) { showError('Failed to load orders: ' + e.message); }
    finally { setLoading(false); }
  }, [apiCall, view, showError]);
  useEffect(() => { setSelected(null); load(); }, [load]);

  const open = async (id) => {
    try { setSelected(await apiCall(`/store/orders/${id}`)); } catch (e) { showError(e.message); }
  };

  const pay = async () => {
    if (!selected) return;
    setPaying(true);
    try {
      await apiCall(`/store/orders/${selected.OrderID}/pay`, { method: 'POST' });
      const receipt = await apiCall(`/store/orders/${selected.OrderID}/receipt`);
      printReceipt(receipt);
      showSuccess(`Paid — receipt #${receipt.ReceiptNumber}`);
      setSelected(null);
      load();
    } catch (e) { showError(e.message); } finally { setPaying(false); }
  };

  const reprint = () => { if (selected) printReceipt(selected); };

  const voidSale = async () => {
    if (!selected) return;
    if (!window.confirm(`Void receipt #${selected.ReceiptNumber}? This reverses the sale and restores the stock.`)) return;
    try {
      await apiCall(`/store/orders/${selected.OrderID}/void`, { method: 'POST' });
      showSuccess('Sale voided — stock restored');
      setSelected(null);
      load();
    } catch (e) { showError(e.message); }
  };

  const tabCls = (v) => `px-3 py-1.5 rounded-lg text-sm font-semibold ${view === v ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`;

  return (
    <div className="grid grid-cols-3 gap-4">
      <div className="col-span-1 bg-white rounded-xl shadow-lg p-4">
        <div className="flex items-center gap-2 mb-3">
          <button onClick={() => setView('pending')} className={tabCls('pending')}>Pending</button>
          <button onClick={() => setView('paid')} className={tabCls('paid')}>Paid / Reprint</button>
          <button onClick={load} className="ml-auto text-gray-400 hover:text-gray-600"><RefreshCw size={16} /></button>
        </div>
        {loading ? <Spinner /> : list.length === 0 ? (
          <p className="text-sm text-gray-500">{view === 'paid' ? 'No paid sales.' : 'No pending orders.'}</p>
        ) : (
          <div className="space-y-2 max-h-[70vh] overflow-y-auto">
            {list.map((o) => (
              <button key={o.OrderID} onClick={() => open(o.OrderID)}
                className={`w-full text-left p-3 rounded-lg border hover:bg-blue-50 ${selected?.OrderID === o.OrderID ? 'border-blue-500 bg-blue-50' : ''}`}>
                <div className="flex justify-between">
                  <span className="font-semibold">#{o.OrderID}{view === 'paid' && o.ReceiptNumber ? ` · Rcpt ${o.ReceiptNumber}` : ''}</span>
                  <span className="text-blue-600 font-semibold">{money(o.TotalAmount)}</span>
                </div>
                <div className="text-xs text-gray-500">{o.FamilyName || o.FamilyID} · {o.ItemCount} item(s) · {fmtDate(view === 'paid' ? o.PaidAt : o.CreatedAt)}</div>
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="col-span-2 bg-white rounded-xl shadow-lg p-6">
        {!selected ? <p className="text-gray-500 text-sm">{view === 'paid' ? 'Select a paid sale to reprint or void.' : 'Select a pending order to take payment.'}</p> : (
          <>
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-lg font-bold">Order #{selected.OrderID}
                  {selected.ReceiptNumber ? <span className="ml-2 text-sm text-gray-500">Receipt #{selected.ReceiptNumber}</span> : null}
                </h3>
                <p className="text-sm text-gray-600">{selected.FamilyName || selected.FamilyID} · Family {selected.FamilyID}
                  {selected.Status === 'Voided' ? <span className="ml-2 text-red-600 font-semibold">VOIDED</span> : null}
                </p>
              </div>
              <button onClick={() => setSelected(null)} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
            </div>
            <table className="min-w-full text-sm mb-4">
              <thead className="bg-gray-50 text-gray-600"><tr>
                <th className="px-3 py-2 text-left">Product</th><th className="px-3 py-2 text-center">Size</th>
                <th className="px-3 py-2 text-right">Unit</th><th className="px-3 py-2 text-center">Qty</th><th className="px-3 py-2 text-right">Total</th>
              </tr></thead>
              <tbody className="divide-y">
                {selected.items.map((it) => (
                  <tr key={it.OrderItemID}>
                    <td className="px-3 py-2">{it.ProductName}</td><td className="px-3 py-2 text-center">{it.Size || '—'}</td>
                    <td className="px-3 py-2 text-right">{money(it.UnitPrice)}</td><td className="px-3 py-2 text-center">{it.Quantity}</td>
                    <td className="px-3 py-2 text-right">{money(it.LineTotal)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="text-right text-xl font-bold mb-4">Total: <span className="text-blue-600">{money(selected.TotalAmount)}</span></div>

            {selected.Status === 'Pending' ? (
              <Gate permission="store.payments.process" label="Process Store Payment">
                <button onClick={pay} disabled={paying}
                  className="w-full flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white font-semibold py-3 rounded-lg disabled:opacity-50">
                  <CheckCircle size={18} /> {paying ? 'Processing…' : 'Accept Payment & Print Receipt'}
                </button>
              </Gate>
            ) : selected.Status === 'Paid' ? (
              <div className="flex gap-3">
                <button onClick={reprint}
                  className="flex-1 flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-lg">
                  <Printer size={18} /> Reprint Receipt
                </button>
                <Gate adminOnly label="Void Store Sale">
                  <button onClick={voidSale}
                    className="flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white font-semibold py-3 px-5 rounded-lg">
                    <Ban size={18} /> Void
                  </button>
                </Gate>
              </div>
            ) : (
              <p className="text-center text-gray-500 text-sm">This sale was voided.</p>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// --------------------------------------------------------------- Catalog tab
function CatalogTab({ apiCall, showSuccess, showError }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editProduct, setEditProduct] = useState(null); // {ProductID?,Name,Category}
  const [variantFor, setVariantFor] = useState(null);   // product to add/edit a variant for
  const [editVariant, setEditVariant] = useState(null); // {VariantID?,Size,UnitPrice,StockQuantity}

  const load = useCallback(async () => {
    try { setLoading(true); setProducts(await apiCall('/store/products?includeInactive=true')); }
    catch (e) { showError('Failed to load catalog: ' + e.message); }
    finally { setLoading(false); }
  }, [apiCall, showError]);
  useEffect(() => { load(); }, [load]);

  const saveProduct = async (p) => {
    try {
      if (p.ProductID) await apiCall(`/store/products/${p.ProductID}`, { method: 'PUT', body: JSON.stringify(p) });
      else await apiCall('/store/products', { method: 'POST', body: JSON.stringify(p) });
      setEditProduct(null); showSuccess('Product saved'); load();
    } catch (e) { showError(e.message); }
  };
  const saveVariant = async (productId, v) => {
    try {
      if (v.VariantID) await apiCall(`/store/variants/${v.VariantID}`, { method: 'PUT', body: JSON.stringify(v) });
      else await apiCall(`/store/products/${productId}/variants`, { method: 'POST', body: JSON.stringify(v) });
      setVariantFor(null); setEditVariant(null); showSuccess('Variant saved'); load();
    } catch (e) { showError(e.message); }
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="font-semibold text-gray-800">Catalog</h2>
        <Gate permission="store.catalog.manage" label="Manage Store Catalog">
          <button onClick={() => setEditProduct({ Name: '', Category: 'Clothes' })}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"><Plus size={18} /> Add Product</button>
        </Gate>
      </div>
      {loading ? <Spinner /> : products.map((p) => (
        <div key={p.ProductID} className={`border rounded-lg p-4 mb-3 ${p.IsActive ? '' : 'opacity-60'}`}>
          <div className="flex justify-between items-center">
            <div>
              <span className="font-semibold text-gray-800">{p.Name}</span>
              {p.Category && <span className="ml-2 text-xs px-2 py-0.5 bg-gray-100 rounded">{p.Category}</span>}
              {!p.IsActive && <span className="ml-2 text-xs text-red-600">inactive</span>}
            </div>
            <div className="flex gap-2">
              <button onClick={() => setEditProduct(p)} className="text-blue-600 hover:text-blue-800" title="Edit product"><Pencil size={16} /></button>
              <button onClick={() => { setVariantFor(p); setEditVariant({ Size: '', UnitPrice: '', StockQuantity: 0 }); }}
                className="text-green-600 hover:text-green-800 text-sm flex items-center gap-1"><Plus size={14} /> size</button>
            </div>
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            {sortVariants(p.variants).map((v) => (
              <button key={v.VariantID} onClick={() => { setVariantFor(p); setEditVariant(v); }}
                className={`text-xs px-2 py-1 rounded border ${v.IsActive ? '' : 'opacity-50 line-through'}`}
                title="Edit / restock">
                {variantLabel(v)} · {money(v.UnitPrice)} · stock <b className={v.StockQuantity <= 3 ? 'text-red-600' : ''}>{v.StockQuantity}</b>
              </button>
            ))}
            {(p.variants || []).length === 0 && <span className="text-xs text-gray-400">No sizes yet — add one.</span>}
          </div>
        </div>
      ))}

      {editProduct && <ProductModal product={editProduct} onSave={saveProduct} onClose={() => setEditProduct(null)} />}
      {variantFor && editVariant && (
        <VariantModal product={variantFor} variant={editVariant}
          onSave={(v) => saveVariant(variantFor.ProductID, v)} onClose={() => { setVariantFor(null); setEditVariant(null); }} />
      )}
    </div>
  );
}

function ProductModal({ product, onSave, onClose }) {
  const [name, setName] = useState(product.Name || '');
  const [category, setCategory] = useState(product.Category || 'Clothes');
  const [isActive, setIsActive] = useState(product.IsActive !== false);
  return (
    <Modal title={product.ProductID ? 'Edit Product' : 'Add Product'} onClose={onClose}>
      <label className="block text-sm font-medium mb-1">Name</label>
      <input value={name} onChange={(e) => setName(e.target.value)} className="w-full border rounded-lg px-3 py-2 mb-3" />
      <label className="block text-sm font-medium mb-1">Category</label>
      <select value={category} onChange={(e) => setCategory(e.target.value)} className="w-full border rounded-lg px-3 py-2 mb-3">
        <option>Clothes</option><option>Books</option><option>Other</option>
      </select>
      {product.ProductID && (
        <label className="flex items-center gap-2 mb-3 text-sm"><input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} /> Active</label>
      )}
      <button onClick={() => onSave({ ProductID: product.ProductID, name: name.trim(), category, isActive })}
        disabled={!name.trim()} className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-lg disabled:opacity-50">Save</button>
    </Modal>
  );
}

function VariantModal({ product, variant, onSave, onClose }) {
  const [size, setSize] = useState(variant.Size || '');
  const [price, setPrice] = useState(variant.UnitPrice ?? '');
  const [stock, setStock] = useState(variant.StockQuantity ?? 0);
  const [isActive, setIsActive] = useState(variant.IsActive !== false);
  return (
    <Modal title={`${variant.VariantID ? 'Edit size' : 'Add size'} — ${product.Name}`} onClose={onClose}>
      <label className="block text-sm font-medium mb-1">Size <span className="text-gray-400">(blank = one-size / book)</span></label>
      <input value={size} onChange={(e) => setSize(e.target.value)} className="w-full border rounded-lg px-3 py-2 mb-3" />
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div><label className="block text-sm font-medium mb-1">Unit price ($)</label>
          <input type="number" step="0.01" value={price} onChange={(e) => setPrice(e.target.value)} className="w-full border rounded-lg px-3 py-2" /></div>
        <div><label className="block text-sm font-medium mb-1">Stock</label>
          <input type="number" value={stock} onChange={(e) => setStock(e.target.value)} className="w-full border rounded-lg px-3 py-2" /></div>
      </div>
      {variant.VariantID && (
        <label className="flex items-center gap-2 mb-3 text-sm"><input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} /> Active</label>
      )}
      <button onClick={() => onSave({ VariantID: variant.VariantID, size: size.trim(), unitPrice: Number(price), stockQuantity: Number(stock), isActive })}
        disabled={price === '' || Number(price) < 0} className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-lg disabled:opacity-50">Save</button>
    </Modal>
  );
}

// ---------------------------------------------------------------- small bits
const Modal = ({ title, onClose, children }) => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
    <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6">
      <div className="flex justify-between items-center mb-4"><h3 className="text-lg font-bold">{title}</h3>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button></div>
      {children}
    </div>
  </div>
);
const Spinner = () => <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" /></div>;
const StatusBadge = ({ s }) => {
  const map = { Pending: 'bg-yellow-100 text-yellow-800', Paid: 'bg-green-100 text-green-800', Cancelled: 'bg-gray-100 text-gray-600' };
  return <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${map[s] || 'bg-gray-100'}`}>{s}</span>;
};

export default Store;
